import React, { useState, useEffect } from 'react';
import querystring from 'querystring';
import Select from 'react-select';
import {
  ListGroup,
  ListGroupItem,
  Card,
  CardFooter,
  Input,
  FormGroup,
  FormText,
} from 'reactstrap';
import { useSession, PseudoStates, CardHeader, Button } from '@cordata/buffet';
import { useLocation } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import PatientMatchingItem from '~/components/IdentityDisputes/PatientMatchingItem';
import SortButton, { SortDirection } from '~/components/common/SortButton';
import { MatchStatus } from '~/utils/const/identity';
import { authorizedInstance as py, useQueryParam } from '~/utils';

interface DisputeMatchingIndexProp {
  matchStatusId: string;
}

interface Division {
  id: string,
  label: string,
  subSpecialtyName?:string,
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100].map(o => ({ label: o, value: o }));

const DisputeMatchingIndex = ({
  matchStatusId,
}: DisputeMatchingIndexProp) => {
  const { current } = useSession();

  const location = useLocation();

  const [expand] = useQueryParam('expand');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortDir, setSortDir] = useState<typeof SortDirection>('descending');
  const [pcsa, setPcsa] = useState<Division[]>([]);
  const [selectedPcsas, setSelectedPcsas] = useState<Division[]>(
    localStorage.getItem('match-filter')
      // @ts-ignore
      ? JSON.parse(localStorage.getItem('match-filter'))[expand]?.divisions || []
      : []);
  const [selectedDivisions, setSelectedDivisions] = useState<Division[]>([]);
  const [status, setStatus] = useState('loading');
  const [data, setData] = useState([]);

  const [page, setPage] = useState<number>(
    localStorage.getItem('match-filter')
      // @ts-ignore
      ? JSON.parse(localStorage.getItem('match-filter'))[expand]?.page || 1
      : 1);
  const [pageSize, setPageSize] = useState<number>(
    localStorage.getItem('match-filter')
      // @ts-ignore
      ? JSON.parse(localStorage.getItem('match-filter'))[expand]?.pageSize || 10
      : 10);
  const [total, setTotal] = useState<number>(0);

  const sortParam = () => {
    if (sortDir === 'ascending') {
      return 'matchStatusDate,lastName,firstName';
    }

    if (sortDir === 'descending') {
      return '-matchStatusDate,lastName,firstName';
    }

    return 'defaultSortOrder,lastName,firstName';
  };

  const unmatchedStatus = matchStatusId === MatchStatus.Unmatched;
  const disputeStatus = matchStatusId === MatchStatus.Disputed;

  const commonParams = {
    ...(unmatchedStatus ? {
      pcsa_division_uid: selectedPcsas.map(sc => sc.id)
    } : {
      division_uid: selectedDivisions.map(sc => sc.id),
    }),
    'page-size': pageSize,
    page,
    query: searchQuery,
    sort: sortParam(),
  };

  const url = unmatchedStatus
    ? 'api/data/public/list-view-cs-tbl-unmatched-source-child'
    : 'api/data/public/list-view-cs-match-queue';

  const matchQueueParams = unmatchedStatus
    ? {
      ...commonParams,
    }
    : {
      distinct: 'sourcepersonid',
      match_status_id: matchStatusId,
      ...commonParams,
    };

  useEffect(() => {
    if (current && unmatchedStatus) {
      py
        .get('api/data/public/identity/nav-list-view-division', {
          params: {
            subSpecialtyName: 'Public Childrens Services Agency',
            sort: 'divisionName',
            'all-data': 1,
          },
          paramsSerializer: querystring.stringify,
        })
        .then((r:any) => r.data.data)
        .then(r => {
          const d = r.map((ad) => ({
            ...ad,
            label: ad.attributes.divisionName,
          }));

          setPcsa(d);
        });
    }
  }, [current]);

  const fetchData = useDebouncedCallback(
    () => py
      .get(url, {
        params: matchQueueParams,
        paramsSerializer: querystring.stringify,
      })
      .then((r: any) => {
        setTotal(r.data.total);
        setData(r.data.data);
        setStatus('success');
      })
      .catch(() => setStatus('error')),
    500,
  );

  useEffect(() => {
    if (searchQuery.length === 0 || searchQuery.length >= 3) {
      setStatus('loading');

      fetchData();
    }
  }, [
    sortDir,
    selectedDivisions,
    selectedPcsas,
    location.state?.refreshModel,
    page,
    pageSize,
    searchQuery,
  ]);

  const sortByDisputeDate = (direction: typeof SortDirection) => {
    setSortDir(direction);
  };

  return (
    <Card>
      <CardHeader
        inverse
        color="primary"
        action={
          !unmatchedStatus
            ? (
              <SortButton
                text={`Sort By ${disputeStatus ? 'Dispute' : 'Match Status'} Date`}
                onToggle={sortByDisputeDate}
                defaultDirection={sortDir}
              />
            ) : <div />
        }
      >
        <FormGroup
          className="w-25 mr-2 my-0"
        >
          <Input
            placeholder="Enter Patient Name"
            onChange={(e: any) => setSearchQuery(e.target.value)}
            text="bob"
          />

          {searchQuery.length > 0 && searchQuery.length < 3 && (
            <FormText color="white">
              Please enter at least 3 characters.
            </FormText>
          )}
        </FormGroup>

        {unmatchedStatus && (
          <Select
            value={selectedPcsas}
            onChange={(d: any) => {
              let currentFilter = localStorage.getItem('match-filter')
                // @ts-ignore
                ? JSON.parse(localStorage.getItem('match-filter'))
                : { [expand]: {} }

              currentFilter[expand] ||= {}

              currentFilter[expand].divisions = d

              localStorage.setItem('match-filter', JSON.stringify(currentFilter))

              setSelectedPcsas(d)
            }}
            options={pcsa}
            getOptionValue={(o: Division) => o.id}
            getOptionLabel={(o: Division) => o.label}
            closeMenuOnSelect={false}
            placeholder="Select County(ies)"
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            className="w-25 mr-auto"
            isMulti
          />
        )}
      </CardHeader>

      <ListGroup className="rounded-0 border-0">
        {status === 'success' ? (
          <>
            {data.length > 0 ? (
              <>
                {data.map((d: any) => (
                  <PatientMatchingItem data={d} key={d.id} />
                ))}
              </>
            ) : (
              <ListGroupItem className="border-0 rounded-bottom">
                No results.
              </ListGroupItem>
            )}
          </>
        ) : (
          <>
            <PseudoStates status={status} />
          </>
        )}
      </ListGroup>

      {total > 0 && (
        <CardFooter className="d-flex align-items-center">
          <Button
            onClick={() => {
              let currentFilter = localStorage.getItem('match-filter')
                // @ts-ignore
                ? JSON.parse(localStorage.getItem('match-filter'))
                : { [expand]: {} }

              currentFilter[expand] ||= {}

              currentFilter[expand].page = page - 1

              localStorage.setItem('match-filter', JSON.stringify(currentFilter))

              setPage(p => p - 1)
            }}
            disabled={page === 1}
            className="flex-grow-1 mr-5"
          >
            Previous
          </Button>

          <span className="mr-5">Page {page} of {Math.ceil(total / pageSize)}</span>

          Show
          <Select
            value={PAGE_SIZE_OPTIONS.find(o => o.value === pageSize)}
            onChange={(o: any) => {
              let currentFilter = localStorage.getItem('match-filter')
                // @ts-ignore
                ? JSON.parse(localStorage.getItem('match-filter'))
                : { [expand]: {} }

              currentFilter[expand] ||= {}

              currentFilter[expand].pageSize = o.value

              localStorage.setItem('match-filter', JSON.stringify(currentFilter))

              setPageSize(o.value)
            }}
            options={PAGE_SIZE_OPTIONS}
            className="text-dark mx-2"
            styles={{
              container: (baseStyles) => ({
                ...baseStyles,
                width: '100px',
              }),
              control: (baseStyles) => ({
                ...baseStyles,
                width: '100px',
              }),
              menu: (baseStyles) => ({
                ...baseStyles,
                width: '100px',
              }),
            }}
            getOptionLabel={(o: any) => o.label}
            getOptionValue={(o: any) => o.value}
          />
          Results Per Page

          <Button
            onClick={() => {
              let currentFilter = localStorage.getItem('match-filter')
                // @ts-ignore
                ? JSON.parse(localStorage.getItem('match-filter'))
                : { [expand]: {} }

              currentFilter[expand] ||= {}

              currentFilter[expand].page = page + 1

              localStorage.setItem('match-filter', JSON.stringify(currentFilter))

              setPage(p => p + 1)
            }}
            disabled={total <= (page * pageSize)}
            className="flex-grow-1 ml-5"
          >
            Next
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default DisputeMatchingIndex;
